import { useRef } from "react";
import { Plus, Check, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { MarginBadge } from "@/components/MarginBadge";
import { PackagingHint } from "@/components/PackagingHint";
import { PriceBlock } from "@/components/PriceBlock";
import { ProductImage } from "@/components/ProductImage";
import { expectedDeliveryDate, formatDeliveryDate, productList, thumbUrl } from "@/lib/product";
import { QuantityStepper } from "@/components/QuantityStepper";
import { StockBadge } from "@/components/StockBadge";
import { veldWaarde, vulPlaceholders } from "@/lib/pdpTemplate";
import { Koopblok } from "@/components/PdpKoopblok";
import { PdpPastBij, PdpTabs } from "@/components/PdpExtra";
import { productText } from "@/lib/product";

/* Swipen (touch of muis) door de foto's: pointer down → up met >= 40px horizontaal verschil. */
const useSwipe = (ctx) => {
  const start = useRef(null);
  const aantal = ctx.images.length;
  const naar = (delta) => ctx.setActiveImage((aantal + ctx.activeImage + delta) % aantal);
  return {
    onPointerDown: (e) => { start.current = { x: e.clientX, y: e.clientY }; },
    onPointerUp: (e) => {
      if (!start.current || aantal < 2) return;
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;
      start.current = null;
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) naar(dx < 0 ? 1 : -1);
    },
    onPointerCancel: () => { start.current = null; },
  };
};

const Galerij = ({ blok, product, ctx }) => {
  const swipe = useSwipe(ctx);
  return (
  <div data-testid="pdp-block-gallery">
    <Card
      className="relative touch-pan-y select-none overflow-hidden border-0 bg-[hsl(var(--photo-bg))] shadow-none [&_[data-testid=margin-badge]]:border [&_[data-testid=margin-badge]]:border-black/10 [&_[data-testid=margin-badge]]:bg-white/85 [&_[data-testid=margin-badge]]:text-foreground [&_[data-testid=margin-badge]]:backdrop-blur"
      data-testid="product-gallery-swipe"
      {...swipe}
    >
      {blok.marge_badge !== false && ctx.account && <MarginBadge product={product} />}
      <ProductImage
        src={ctx.images[ctx.activeImage]}
        alt={ctx.t("product.imageAlt", { naam: ctx.naam })}
        className="aspect-[4/3] w-full bg-[hsl(var(--photo-bg))]"
        imgClassName="p-8"
        width={1200}
      />
      {ctx.images.length > 1 && (
        <div className="absolute inset-x-0 bottom-8 flex justify-center gap-1.5 sm:hidden" data-testid="product-image-dots">
          {ctx.images.map((src, index) => (
            <button
              key={src}
              type="button"
              onClick={() => ctx.setActiveImage(index)}
              className={`h-2 rounded-full border border-black/10 transition-all ${index === ctx.activeImage ? "w-5 bg-foreground" : "w-2 bg-white/80"}`}
              aria-label={`${ctx.naam} ${index + 1}`}
              data-testid={`product-image-dot-${index}`}
            />
          ))}
        </div>
      )}
    </Card>
    {ctx.images.length > 1 && (
      <div className="mt-3 hidden flex-wrap gap-2 sm:flex" data-testid="product-thumbnails">
        {ctx.images.map((src, index) => (
          <button
            key={src}
            type="button"
            onClick={() => ctx.setActiveImage(index)}
            className={`h-16 w-16 overflow-hidden rounded-md border bg-[hsl(var(--photo-bg))] p-1 ${
              index === ctx.activeImage ? "ring-2 ring-ring" : ""
            }`}
            data-testid={`product-thumbnail-${index}`}
            aria-label={`${ctx.naam} ${index + 1}`}
          >
            <img src={thumbUrl(src, 240, { alpha: true })} alt="" className="h-full w-full object-contain" />
          </button>
        ))}
      </div>
    )}
  </div>
  );
};

const Omschrijving = ({ product, ctx }) => (
  <div data-testid="pdp-block-omschrijving">
    <h2 className="font-display text-lg font-semibold">{ctx.t("product.description")}</h2>
    {ctx.omschrijving ? (
      <p
        className="mt-3 whitespace-pre-line text-[15px] leading-7 text-foreground/70"
        data-testid="product-description"
      >
        {ctx.omschrijving}
      </p>
    ) : (
      <p className="mt-3 text-sm text-muted-foreground" data-testid="product-description-empty">
        {ctx.t("product.noDescription")}
      </p>
    )}
  </div>
);

const AantalBlok = ({ product, ctx }) => (
  <div className="space-y-3" data-testid="pdp-block-aantal">
    {!ctx.deliverable && (
      <p className="rounded-md border bg-secondary px-3 py-2 text-xs text-muted-foreground" data-testid="product-country-note">
        {ctx.t("stock.countryBlocked")}
      </p>
    )}
    {ctx.deliverable && !ctx.orderable && (
      <p className="rounded-md border bg-secondary px-3 py-2 text-xs text-muted-foreground" data-testid="product-unavailable-note">
        {ctx.t("stock.unavailableNote")}
      </p>
    )}
    {ctx.orderable && ctx.supplier && (
      <p
        className="rounded-md border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs font-medium text-[hsl(var(--warning))]"
        data-testid="product-supplier-note"
      >
        {ctx.t("stock.supplierNote", { datum: formatDeliveryDate(expectedDeliveryDate(), ctx.lang) })}
      </p>
    )}
    {ctx.orderable && ctx.outOfStock && (
      <p
        className="rounded-md border border-[hsl(var(--danger))]/25 bg-[hsl(var(--danger))]/10 px-3 py-2 text-xs text-[hsl(var(--danger))]"
        data-testid="product-backorder-note"
      >
        {ctx.t("stock.backorder")}
      </p>
    )}
    <Label htmlFor="product-quantity">{ctx.t("product.quantity")}</Label>
    <div className="flex flex-wrap items-center gap-3">
      <QuantityStepper
        product={product}
        value={ctx.aantal}
        onChange={ctx.setAantal}
        disabled={!ctx.orderable}
        inputId="product-quantity"
        testIdPrefix="product-quantity"
      />
      <Button
        className="min-w-[180px] flex-1 whitespace-normal"
        onClick={ctx.onAdd}
        disabled={!ctx.orderable}
        data-testid="product-add-to-cart-button"
      >
        <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
        {ctx.orderable
          ? ctx.t("product.addToCart")
          : ctx.deliverable
            ? ctx.t("stock.unavailable")
            : ctx.t("stock.countryBlockedShort")}
      </Button>
    </div>
  </div>
);

const Inci = ({ product, ctx }) => {
  const waarde = veldWaarde("inci", product, ctx);
  if (!waarde) return null;
  return (
    <div data-testid="pdp-block-inci">
      <h2 className="font-display text-lg font-semibold">{ctx.t("pdp.inciTitle")}</h2>
      <p className="mt-2 break-words text-xs leading-6 text-muted-foreground" data-testid="pdp-block-inci-body">
        {waarde}
      </p>
    </div>
  );
};

/* USP's en claims samen in één rustig, scanbaar blok. */
const Argumenten = ({ product, ctx }) => {
  const usps = productList(product, "usp", ctx.lang).slice(0, 4);
  const claims = productList(product, "claims", ctx.lang).slice(0, 4);
  if (!usps.length && !claims.length) return null;
  return (
    <section className="rounded-xl border border-border/70 bg-secondary/40 px-4 py-4" data-testid="pdp-block-argumenten">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{ctx.t("pdp.uspTitle")}</h2>
      {usps.length > 0 && (
        <ul className="mt-3 space-y-2">
          {usps.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm leading-6 text-foreground/80" data-testid={`pdp-usp-${i}`}>
              <Check className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {claims.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 ${usps.length ? "mt-4 border-t border-border/60 pt-3" : "mt-3"}`} data-testid="pdp-block-claims" aria-label={ctx.t("pdp.claimsTitle")}>
          {claims.map((item, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground/75" data-testid={`pdp-claim-${i}`}>
              <BadgeCheck className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              {item}
            </span>
          ))}
        </div>
      )}
    </section>
  );
};

const Usps = ({ product, ctx }) => {
  const items = productList(product, "usp", ctx.lang).slice(0, 3);
  if (!items.length) return null;
  return (
    <div data-testid="pdp-block-usp">
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm" data-testid={`pdp-usp-${i}`}>
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Claims = ({ product, ctx }) => {
  const items = productList(product, "claims", ctx.lang).slice(0, 3);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="pdp-block-claims" aria-label={ctx.t("pdp.claimsTitle")}>
      {items.map((item, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-xs font-medium text-foreground/80"
          data-testid={`pdp-claim-${i}`}
        >
          <BadgeCheck className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          {item}
        </span>
      ))}
    </div>
  );
};

const VeldenTabel = ({ blok, product, ctx }) => {
  const rijen = (blok.velden || [])
    .map((key) => ({ key, label: ctx.t(`pdp.field.${key}`), value: veldWaarde(key, product, ctx) }))
    .filter((r) => r.value != null && r.value !== "");
  if (rijen.length === 0) return null;
  return (
    <dl className="space-y-2 text-sm" data-testid="pdp-block-velden">
      {rijen.map((r) =>
        String(r.value).length > 60 ? (
          <div key={r.key} data-testid={`pdp-field-${r.key}`}>
            <dt className="text-muted-foreground">{r.label}</dt>
            <dd className="mt-1 break-words text-xs leading-6">{r.value}</dd>
          </div>
        ) : (
          <div key={r.key} className="flex justify-between gap-4" data-testid={`pdp-field-${r.key}`}>
            <dt className="text-muted-foreground">{r.label}</dt>
            <dd className="text-right font-medium tabular-nums">{r.value}</dd>
          </div>
        ),
      )}
    </dl>
  );
};

const VrijeTekst = ({ blok, product, ctx }) => {
  const titel = blok.titel?.[ctx.lang] || blok.titel?.nl || "";
  const ruwe = blok.tekst?.[ctx.lang] || blok.tekst?.nl || "";
  const tekst = vulPlaceholders(ruwe, product, ctx);
  if (!tekst && !titel) return null;
  return (
    <div data-testid="pdp-block-tekst">
      {titel && <h2 className="font-display text-base font-semibold">{titel}</h2>}
      {tekst && (
        <p className="mt-2 whitespace-pre-line text-sm leading-7 text-muted-foreground" data-testid="pdp-block-tekst-body">
          {tekst}
        </p>
      )}
    </div>
  );
};

export const PdpBlock = ({ blok, product, ctx }) => {
  switch (blok.type) {
    case "gallery":
      return <Galerij blok={blok} product={product} ctx={ctx} />;
    case "titel": {
      const inhoud = String(product?.inhoud || "").trim();
      const metInhoud = inhoud && !/^\d+$/.test(inhoud) && !ctx.naam.toLowerCase().includes(inhoud.toLowerCase());
      return (
        <h1 className="font-display text-2xl font-semibold leading-tight tracking-tight" data-testid="product-detail-title">
          {ctx.naam}
          {metInhoud && <span className="ml-2 text-lg font-normal text-muted-foreground" data-testid="product-detail-inhoud">{inhoud}</span>}
        </h1>
      );
    }
    case "merk":
      return product?.merk ? (
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground" data-testid="pdp-block-merk">{product.merk}</p>
      ) : null;
    case "korte_omschrijving": {
      const kort = productText(product, "korte_omschrijving", ctx.lang) || product?.korte_omschrijving || "";
      return kort ? <p className="text-[15px] leading-7 text-foreground/70" data-testid="pdp-block-korte-omschrijving">{kort}</p> : null;
    }
    case "koopblok":
      return <Koopblok product={product} ctx={ctx} />;
    case "tabs":
      return <PdpTabs blok={blok} product={product} ctx={ctx} />;
    case "past_bij":
      return <PdpPastBij product={product} ctx={ctx} />;
    case "sku":
      return (
        <div className="flex items-start justify-between gap-3" data-testid="pdp-block-sku">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {product.sku}
          </span>
          <StockBadge product={product} />
        </div>
      );
    case "voorraad":
      return <StockBadge product={product} />;
    case "prijs":
      return <PriceBlock product={product} size="lg" showVatRate />;
    case "aantal":
      return <AantalBlok product={product} ctx={ctx} />;
    case "verpakking":
      return <PackagingHint product={product} testId="product-packaging-hint" />;
    case "omschrijving":
      return <Omschrijving product={product} ctx={ctx} />;
    case "inci":
      return <Inci product={product} ctx={ctx} />;
    case "usp":
      return <Usps product={product} ctx={ctx} />;
    case "argumenten":
      return <Argumenten product={product} ctx={ctx} />;
    case "claims":
      return <Claims product={product} ctx={ctx} />;
    case "velden":
      return <VeldenTabel blok={blok} product={product} ctx={ctx} />;
    case "tekst":
      return <VrijeTekst blok={blok} product={product} ctx={ctx} />;
    case "lijn":
      return <Separator />;
    default:
      return null;
  }
};
